import styles from '../shared/mhlShared.module.scss'
import { REVIEW_LABELS } from '../shared/mhlFormatting'
import { ToggleInput } from '../components/ToggleInput/ToggleInput'
import type {
    MhlAudiologyFilter,
    MhlChannelOptions,
    MhlEsignFilter,
    MhlLeadFilterState,
    MhlQuantumFilter,
    MhlReviewFilter,
} from './MhlLeadsFilter'

interface Props {
    filters: MhlLeadFilterState
    onChange: (next: MhlLeadFilterState) => void
    /** Review filter only applies to dashboard-onboarded leads. */
    showReview?: boolean
    channelOptions: MhlChannelOptions
}

const QUANTUM_OPTIONS: { value: MhlQuantumFilter; label: string }[] = [
    { value: 'all', label: 'All' },
    { value: 'above', label: 'Above' },
    { value: 'below', label: 'Below' },
    { value: 'tbc', label: 'TBC' },
]

const AUDIOLOGY_OPTIONS: { value: MhlAudiologyFilter; label: string }[] = [
    { value: 'all', label: 'All' },
    { value: '>=4dB', label: '≥4 dB' },
    { value: '<4dB', label: '<4 dB' },
    { value: 'tbc', label: 'TBC' },
]

const ESIGN_OPTIONS: { value: MhlEsignFilter; label: string }[] = [
    { value: 'all', label: 'All' },
    { value: 'not_sent', label: 'Not sent' },
    { value: 'sent', label: 'Sent' },
    { value: 'signed', label: 'Signed' },
]

const extraActive = (filters: MhlLeadFilterState, showReview: boolean) =>
    [
        filters.channel !== 'all',
        filters.quantum !== 'all',
        filters.audiology !== 'all',
        filters.esign !== 'all',
        showReview && filters.review !== 'all',
        !!filters.from,
        !!filters.to,
    ].filter(Boolean).length

/**
 * Lead-tracker use-case body for {@link FloatingPanel}.
 * Extra filters only — search and sort stay in the toolbar row.
 */
export const MhlLeadsFilterPanelContent = ({ filters, onChange, showReview = false, channelOptions }: Props) => {
    const patch = (partial: Partial<MhlLeadFilterState>) => onChange({ ...filters, ...partial })
    const active = extraActive(filters, showReview)

    return (
        <div className={styles.filterPanelStack}>
            <select
                className={styles.select}
                value={filters.channel}
                onChange={(e) => patch({ channel: e.target.value })}
                aria-label="Agent or source"
            >
                <option value="all">All agents &amp; sources</option>
                {channelOptions.agents.length > 0 && (
                    <optgroup label="Agents">
                        {channelOptions.agents.map((o) => (
                            <option key={o.value} value={o.value}>
                                {o.label}
                            </option>
                        ))}
                    </optgroup>
                )}
                {channelOptions.website.length > 0 && (
                    <optgroup label="Sources">
                        {channelOptions.website.map((o) => (
                            <option key={o.value} value={o.value}>
                                {o.label}
                            </option>
                        ))}
                    </optgroup>
                )}
            </select>
            {showReview && (
                <select
                    className={styles.select}
                    value={filters.review}
                    onChange={(e) => patch({ review: e.target.value as MhlReviewFilter })}
                    aria-label="Review status"
                >
                    <option value="all">All review states</option>
                    <option value="awaiting_review">{REVIEW_LABELS.awaiting_review}</option>
                    <option value="approved">{REVIEW_LABELS.approved}</option>
                    <option value="rejected">{REVIEW_LABELS.rejected}</option>
                    <option value="not_required">{REVIEW_LABELS.not_required}</option>
                </select>
            )}
            <ToggleInput
                label="Quantum"
                value={filters.quantum}
                options={QUANTUM_OPTIONS}
                onChange={(quantum) => patch({ quantum })}
            />
            <ToggleInput
                label="Audiology"
                value={filters.audiology}
                options={AUDIOLOGY_OPTIONS}
                onChange={(audiology) => patch({ audiology })}
            />
            <ToggleInput
                label="E-sign"
                value={filters.esign}
                options={ESIGN_OPTIONS}
                onChange={(esign) => patch({ esign })}
            />
            <div className={styles.filterFieldGroup}>
                <label className={styles.filterField}>
                    <span className={styles.filterFieldLabel}>Submitted from</span>
                    <input
                        type="date"
                        className={styles.dateInput}
                        value={filters.from}
                        onChange={(e) => patch({ from: e.target.value })}
                    />
                </label>
                <label className={styles.filterField}>
                    <span className={styles.filterFieldLabel}>Submitted to</span>
                    <input
                        type="date"
                        className={styles.dateInput}
                        value={filters.to}
                        onChange={(e) => patch({ to: e.target.value })}
                    />
                </label>
            </div>
            {active > 0 && (
                <button
                    type="button"
                    className={`${styles.btn} ${styles.btnGhost}`}
                    onClick={() =>
                        onChange({
                            ...filters,
                            channel: 'all',
                            quantum: 'all',
                            audiology: 'all',
                            esign: 'all',
                            review: 'all',
                            from: '',
                            to: '',
                        })
                    }
                >
                    Clear filters
                </button>
            )}
        </div>
    )
}
